<template>
  <div
    :class="[
      'owc-simple-list-item',
      {
        'owc-simple-list-item--contact': isContactItem,
        'owc-simple-list-item--download': isDownloadItem,
        'owc-simple-list-item--link': isLinkItem,
      },
    ]"
  >
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <div class="owc-simple-list-item__wrapper">
      <div v-if="hasImage" class="owc-simple-list-item__media">
        <template v-if="isLinkItem && isLinkProvided">
          <a :href="details.url" :title="details.label" :target="details.target">
            <GenericImage :imageData="imageData" class="owc-simple-list-item__image"></GenericImage>
          </a>
        </template>
        <template v-else>
          <GenericImage :imageData="imageData" class="owc-simple-list-item__image"></GenericImage>
        </template>
      </div>
      <div :class="['owc-simple-list-item__inner', { 'owc-simple-list-item__inner--only': !hasImage }]">
        <section class="owc-simple-list-item__content">
          <div class="owc-simple-list-item__tags">
            <wb-tag variant="tertiary" v-for="tag in tags" :key="tag" class="owc-simple-list-item__tag">
              {{ tag }}
            </wb-tag>
          </div>
          <div>
            <h2 class="owc-simple-list-item__headline wb-type-heading-xs" v-html="headline"></h2>
            <div
              v-if="description"
              class="owc-simple-list-item__description wb-type-copy"
              v-html="description"
            ></div>
            <div v-if="location" class="owc-simple-list-item__location wb-type-copy">
              {{ location }}
            </div>
            <div v-if="details && !isLinkItem" class="owc-simple-list-item__details">
              <template v-if="isContactItem">
                <p>{{ details.phone }}</p>
                <p>{{ details.email }}</p>
              </template>
              <template v-else-if="isDownloadItem">
                <p class="owc-simple-list-item__extension">{{ details.fileExtension }}</p>
                <p>{{ details.fileSize }}</p>
              </template>
            </div>
          </div>
        </section>
        <section class="owc-simple-list-item__buttons">
          <template v-if="isContactItem">
            <a
              :href="`mailto:${details.email}`"
              title="Send e-mail"
              @click="mailTracking"
              class="owc-simple-list-item__button wb-link wb-round-button wb-round-button--solid-level-0 wb-round-button--medium"
              data-track="false"
            >
              <wb-icon
                class="wb-round-button__icon owc-simple-list-item__buttonIcon"
                aria-hidden="true"
                name="bds/mail/24"
                flip-rtl
              ></wb-icon>
              Send e-mail
            </a>
            <a
              :href="`tel:${details.phone}`"
              title="Make a call"
              class="owc-simple-list-item__button wb-link wb-round-button wb-round-button--solid-level-0 wb-round-button--medium"
              @click="phoneTracking"
              data-track="false"
            >
              <wb-icon
                class="wb-round-button__icon owc-simple-list-item__buttonIcon"
                aria-hidden="true"
                name="bds/call/24"
                flip-rtl
              ></wb-icon>
              Make a call
            </a>
          </template>
          <template v-else-if="isDownloadItem">
            <a
              :href="details.filePath"
              :download="details.fileTitle"
              title="Download"
              class="owc-simple-list-item__button wb-link wb-round-button wb-round-button--solid-level-0 wb-round-button--medium"
              @click="downloadTracking"
              data-track="false"
            >
              <wb-icon
                class="wb-round-button__icon owc-simple-list-item__buttonIcon"
                aria-hidden="true"
                name="bds/download/24"
                flip-rtl
              ></wb-icon>
              Download
            </a>
          </template>
          <template v-else-if="isLinkItem && isLinkProvided">
            <a
              :href="details.url"
              :title="details.label"
              :target="details.target"
              :data-id="id"
              class="owc-simple-list-item__button wb-link wb-round-button wb-round-button--solid-level-0 wb-round-button--medium"
              @click="linkTracking"
              data-track="false"
            >
              <wb-icon
                class="wb-round-button__icon owc-simple-list-item__buttonIcon"
                aria-hidden="true"
                name="bds/chevron-right/24"
                flip-rtl
              ></wb-icon>
              Open link
            </a>
          </template>
        </section>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import GenericImage from '@owc-packages/GenericImage/src/GenericImage.vue';
import { GenericImage as GenericImageType } from '@owc-utils/GenericImage/Image';
import { SimpleListItemComponent } from './SimpleListItem.d';
import { SimpleListType } from './SimpleList.enums';
import { IMAGE_RATIO } from '@/packages/SimpleList/src/SimpleList.config';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { TrackingInstance } from '@owc-packages/Tracking/src/Tracking';
import initializeTracking, { formatTrackingString } from '@owc-packages/Tracking/src/TrackingUtils';
import { SimpleListDownloadItem, SimpleListLinkItem } from './SimpleList';

let trackingInstance: TrackingInstance | null = null;

export default (ComponentInitializeDispatcher as SimpleListItemComponent).extend({
  name: 'owc-simple-list-item',
  mixins: [OwcDataComponent],
  components: {
    GenericImage,
    ValidationBoxWrapper,
  },
  async mounted() {
    trackingInstance = await initializeTracking();
  },
  computed: {
    isContactItem() {
      return this.type === SimpleListType.Contact;
    },
    isDownloadItem() {
      return this.type === SimpleListType.Download;
    },
    isLinkItem() {
      return this.type === SimpleListType.Link;
    },
    type() {
      return this.componentData?.type;
    },
    headline() {
      return this.componentData?.headline || this.componentData?.name;
    },
    description() {
      return this.componentData?.description || this.componentData?.positionRole;
    },
    location() {
      return this.componentData?.location;
    },
    tags() {
      return this.componentData?.tags || [];
    },
    id() {
      return this.componentData?.id;
    },
    imageData() {
      switch (this.type) {
        case SimpleListType.Contact:
        case SimpleListType.Link:
          return {
            ...this.componentData?.image,
            ratio: IMAGE_RATIO,
          } as GenericImageType;
        default:
          return null;
      }
    },
    details() {
      switch (this.type) {
        case SimpleListType.Contact:
          return {
            phone: this.componentData?.phone,
            email: this.componentData?.email,
          };
        case SimpleListType.Download:
          return {
            fileSize: this.componentData?.fileSize,
            filePath: this.componentData?.filePath,
            fileTitle: this.componentData?.fileTitle,
            fileExtension: (this.componentData?.fileExtension || '').toUpperCase(),
          };
        case SimpleListType.Link:
          return this.componentData?.buttonData;
        default:
          return undefined;
      }
    },
    hasImage() {
      return Boolean(this.componentData?.image?.source);
    },
    isLinkProvided() {
      return Boolean(this.componentData?.buttonData?.url);
    },
  },
  methods: {
    issueTracking(label: string, category: string, el: Element) {
      if (trackingInstance && this.json) {
        trackingInstance.customTrackingObjectUpdate(
          {
            label,
            category,
          },
          'click',
          el,
        );
      }
    },
    linkTracking(event) {
      const contentTitle = this.headline || '';
      const linkTitle = (this.details as Partial<SimpleListLinkItem['buttonData']>)?.label || '';
      this.issueTracking(
        `${formatTrackingString(linkTitle)}.${formatTrackingString(contentTitle)}`,
        'link',
        event.target as Element,
      );
    },
    downloadTracking(event) {
      const filePath = (this.details as Partial<SimpleListDownloadItem>).filePath || '';
      const fileName = filePath.substring(filePath.lastIndexOf(`/`) + 1);
      this.issueTracking(`${formatTrackingString(fileName)}`, 'download', event.target as Element);
    },
    phoneTracking(event) {
      this.issueTracking(
        `make_a_call.${formatTrackingString(this.headline || '')}`,
        'link',
        event.target as Element,
      );
    },
    mailTracking(event) {
      this.issueTracking(
        `send_email.${formatTrackingString(this.headline || '')}`,
        'link',
        event.target as Element,
      );
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/rtl-utils';

$simpleListSpacing: 8px;
$simpleListTagsMinHeight: 30px;

.owc-simple-list-item {
  box-shadow: var(--wb-shadow-level-1);

  &__wrapper {
    @include wb-grid-gaps;
    @include wb-content-width;

    display: grid;
    grid-template-columns: repeat(12, 1fr);
    height: 100%;

    @include breakpoint-from(mq3) {
      width: 100%;
    }
    @include breakpoint-from(mq5) {
      grid-template-columns: repeat(6, 1fr);
    }
  }
  &__media {
    grid-column: 1 / span 4;

    @include breakpoint-from(mq3) {
      grid-column-end: span 2;
    }
  }
  &__image {
    width: auto;
    height: 100%;

    pointer-events: none;
  }
  &__inner {
    display: flex;
    grid-column: 5 / -1;
    // Negative margin to get rid of the grid gap and make sure the spacing is XS
    margin-left: calc(var(--wb-grid-gutter-width) * -1);

    @include rtl-rule(&, false) {
      margin-right: calc(var(--wb-grid-gutter-width) * -1);
      margin-left: initial;
    }

    padding: var(--wb-spacing-xs) 0;

    @include breakpoint-from(mq3) {
      grid-column-start: 3;
      padding: var(--wb-spacing-xs);
    }

    &:not(&--only) {
      padding-left: var(--wb-spacing-xs);
    }
    &--only {
      grid-column-start: 1;
      align-items: center;
      margin-left: 0;

      @include breakpoint-from(mq5) {
        align-items: flex-start;
      }
    }
  }
  &__content {
    flex: 1;

    margin-right: var(--wb-spacing-m);

    @include rtl-rule(&, false) {
      margin-right: initial;
      margin-left: var(--wb-spacing-m);
    }
  }
  &__tags {
    display: none;

    @include breakpoint-from(mq5) {
      display: block;
      min-height: $simpleListTagsMinHeight;
      margin-bottom: $simpleListSpacing * 2;
    }
  }
  &__tag {
    margin-right: $simpleListSpacing;
  }
  &__headline,
  &__description {
    word-break: normal;
    overflow-wrap: anywhere;
  }
  &__description {
    display: -webkit-box;
    margin-top: var(--wb-spacing-xxs);
    overflow: hidden;

    text-overflow: ellipsis;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;

    @include breakpoint-from(mq5) {
      margin-top: 0;
    }
  }
  &__details {
    @include wb-type-hint;
    margin-top: var(--wb-spacing-xs);

    @include breakpoint-from(mq3) {
      margin-top: $simpleListSpacing * 2;
    }
  }
  &__buttons {
    display: flex;
    flex-direction: column;
    align-self: center;

    @include breakpoint-between(mq3, mq5) {
      flex-direction: row;
    }
  }
  &__button {
    display: flex;

    &:first-child + & {
      margin-top: var(--wb-spacing-xs);

      @include breakpoint-between(mq3, mq5) {
        margin-top: 0;
        margin-left: var(--wb-spacing-xs);
      }
    }
  }
  &__buttonIcon {
    pointer-events: none;
  }
  &__extension {
    margin-right: var(--wb-spacing-xs);
  }
  &--contact {
    .owc-simple-list-item__details {
      display: none;

      @include breakpoint-from(mq5) {
        display: block;
      }
    }
  }
  &--download {
    .owc-simple-list-item__inner {
      align-items: center;
    }
    .owc-simple-list-item__details {
      @include wb-type-copy-secondary;

      display: flex;

      color: var(--wb-grey-60);

      @include breakpoint-from(mq3) {
        margin-top: 0;
      }
    }
    .owc-simple-list-item__tags:empty {
      display: none;
    }
  }
}
</style>
